import { useEffect, useState, Fragment } from 'react';
import { databaseDateToFilename, useStatistics, useWindowDimensions } from "./Utilities"
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { getData } from '../api/dataSlice';
import { useSelector } from "react-redux"
import { Grid, Box } from '@mui/material'
import Static from "./live/Static"
import StaticWithLive from './live/StaticWithLive';
import Timelapse from "./timelapse/Timelapse"
import Archive from "./archive/Archive"
import ArchiveControls from './archive/ArchiveControls'
import AccessMonitoring from './access/AccessMonitoring'
import AccessMonitoringControls from './access/AccessMonitoringControls';
import DashboardContent from './dashboard/DashboardContent';

export default function Content({ component }) {

    const { CAMERAS, LAYOUT: { WRAP_LIMIT }, DEBUG } = window.conf
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { index, source, initialFooterHeight, accessMonitoringCameraId } = useSelector(getData)
    const { contentHeight } = useWindowDimensions()
    
    const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false)
    const [paddingFooter, setPaddingFooter] = useState(0)
    const [showDashboard, setShowDashboard] = useState(false)
    const [dashboardState, setDashboardState] = useState(null)
    
    const isArchive = component === 3
    const isMySite = component === 4
    const isAccessMonitoring = component === 5
    const drawerBleeding = 22
    const initialSiderWidth = isMobile && isLandscape ? 235 : 332
    const camCount = isAccessMonitoring ? 2 : CAMERAS.length
    const manyCameras = camCount > WRAP_LIMIT
    const hasControls = isArchive || isMySite || isAccessMonitoring
    const isStatisticsLoaded = useStatistics()
    const selectedAccessMonitoringCameraId = CAMERAS.find(elem => elem?.id === accessMonitoringCameraId) ?? CAMERAS[0]

    useEffect(() => {
        if (!isMySite) setShowDashboard(false)
    }, [component]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        isStatisticsLoaded && DEBUG && console.log("[etracker] Successfully initialized")
    }, [isStatisticsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

    function renderComponent(camera, renderIndex) {

        switch (component) {

            // HOME
            case 0:
                return (
                    <Static
                        contentHeight={contentHeight}
                        camera={camera}
                    />
                )

            // LIVE
            case 1:
                if (camera.video) {
                    return (
                        <StaticWithLive
                            contentHeight={contentHeight}
                            camera={camera}
                        />
                    )
                } else {
                    return (
                        <Static
                            contentHeight={contentHeight}
                            camera={camera}
                        />
                    )
                }

            // TIMELAPSE
            case 2:
                return (
                    <Timelapse
                        contentHeight={contentHeight}
                        camera={camera}
                    />
                )

            // ARCHIVE & MYSITE
            case 3:
            case 4:
                let imageSource = camera.image
                
                if (source && Number.isFinite(index)) {
                    imageSource = camera.download + databaseDateToFilename(source[0]?.[index]?.[1]?.[camera.id]?.datum)
                }

                return (
                    <Archive
                        contentHeight={isMobile && isPortrait ? contentHeight - initialFooterHeight : contentHeight}
                        camera={camera}
                        imageSource={imageSource}
                        imageIndex={index}
                        imagesList={source ? source : []}
                        isSideDrawerOpen={isSideDrawerOpen}
                        paddingFooter={paddingFooter}
                    />
                )

            // ACCESS MONITORING
            case 5:
                return (
                    <AccessMonitoring
                        camera={selectedAccessMonitoringCameraId}
                        direction={renderIndex === 0 ? "entry" : "exit"}
                    />
                )

            default:
                break;
        }
    }

    let camerasToRender = CAMERAS;

    if (isAccessMonitoring) {
        camerasToRender = [selectedAccessMonitoringCameraId, selectedAccessMonitoringCameraId]
    }

    const contentWidth = hasControls
        ? isMobile
            ? isLandscape
                ? isSideDrawerOpen
                    ? `calc(100% - ${initialSiderWidth}px)`
                    : `calc(100% - ${drawerBleeding}px)`
                : '100%'
            : `calc(100% - ${initialSiderWidth}px)`
        : '100%'

    return (
        <Fragment>
            {showDashboard ? (
                <Box
                    sx={{
                        display: 'block',
                        width: contentWidth,
                        height: contentHeight,
                        overflow: 'auto',
                        background: '#f5f6fa',
                        transition: '0.2s linear'
                    }}
                >
                    <DashboardContent dashboardState={dashboardState} contentHeight={contentHeight} />
                </Box>
            ) : (
                <Grid
                    container
                    columns={{
                        xs: !isMobile && isAccessMonitoring ? 2 : 1,
                        md: !isMobile && 2,
                        lg: isAccessMonitoring ? 2 : (manyCameras ? 3 : 2),
                        xl: isAccessMonitoring ? 2 : (manyCameras ? 4 : (camCount > 2 ? 3 : 2))
                    }}
                    sx={{
                        marginBottom: hasControls && isMobile && isPortrait ? `${initialFooterHeight}px` : 0,
                        width: contentWidth,
                        transition: '0.2s linear',
                        background: "black",
                        overflowX: "hidden",
                        paddingBottom: hasControls && isMobile && isPortrait ? `${paddingFooter}px` : 0,
                    }}
                >
                    {camerasToRender.map((camera, index) => (
                        <Grid
                            item
                            key={index}
                            xs={1}
                            md={camCount % 2 // amount of images is odd (e.g. 1, 3 or 5)
                                && index === camCount - 1 // image is last element of the array
                                && !manyCameras // do not extend last item for manyCameras grid
                                && !isAccessMonitoring // do not extend last item for access monitoring
                                ? 2
                                : 1
                            }
                            xl={!isAccessMonitoring && camCount > 2 ? 1 : undefined}
                            sx={{
                                backgroundColor: "black", // TODO: change background color of images/video
                                overflow: "hidden",
                                height: isAccessMonitoring && !isMobile ? contentHeight : 'auto' // ensure full height for scrolling columns
                            }}
                        >
                            {renderComponent(camera, index)}
                        </Grid>
                    ))}
                </Grid>
            )}

            {(isMySite || isArchive) && (
                <ArchiveControls
                    initialSiderWidth={initialSiderWidth}
                    onOpenSideDrawer={setIsSideDrawerOpen}
                    setPaddingFooter={setPaddingFooter}
                    drawerBleeding={drawerBleeding}
                    component={component}
                    onShowDashboard={setShowDashboard}
                    showDashboard={showDashboard}
                    onDashboardStateChange={setDashboardState}
                />
            )}

            {isAccessMonitoring && (
                <AccessMonitoringControls
                    initialSiderWidth={initialSiderWidth}
                    onOpenSideDrawer={setIsSideDrawerOpen}
                    setPaddingFooter={setPaddingFooter}
                    drawerBleeding={drawerBleeding}
                />
            )}

        </Fragment>
    )
}